import { useNavigate } from "react-router-dom"
import { FileText, Plus, MoreHorizontal, Send, CheckCircle, XCircle, Trash2, Copy } from "lucide-react"
import { ExportButton } from "@/components/export-button"
import { useColumnPreferences } from "@/hooks/use-column-preferences"
import { useFilterParams } from "@/hooks/use-filter-params"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ColumnSelector, type ColumnOption } from "@/components/column-selector"
import { FilterPanel, SelectFilter, DateRangeFilter } from "@/components/filters"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { toast } from "sonner"
import { useQuotes, useSendQuote, useAcceptQuote, useRejectQuote, useDeleteQuote, useNewQuoteVersion } from "@/hooks/use-quotes"
import type { Quote, QuoteStatus } from "@/lib/types"

const STATUS_CONFIG: Record<QuoteStatus, { label: string; color: string; bg: string }> = {
  draft: { label: "Szkic", color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)" },
  sent: { label: "Wysłana", color: "#3B82F6", bg: "rgba(59, 130, 246, 0.1)" },
  viewed: { label: "Wyświetlona", color: "#8B5CF6", bg: "rgba(139, 92, 246, 0.1)" },
  accepted: { label: "Zaakceptowana", color: "#10B981", bg: "rgba(16, 185, 129, 0.1)" },
  rejected: { label: "Odrzucona", color: "#EF4444", bg: "rgba(239, 68, 68, 0.1)" },
  expired: { label: "Wygasła", color: "#F59E0B", bg: "rgba(245, 158, 11, 0.1)" },
  cancelled: { label: "Anulowana", color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)" },
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return "-"
  return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format(amount)
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-"
  return new Date(dateStr).toLocaleDateString("pl-PL")
}

const QUOTE_FILTER_KEYS = [
  "status", "issue_date_from", "issue_date_to", "valid_until_from", "valid_until_to",
] as const

const QUOTE_COLUMN_IDS = ["number", "company", "status", "total_net", "total_gross", "issue_date", "valid_until", "owner"]
const DEFAULT_VISIBLE = ["number", "company", "status", "total_net", "total_gross", "issue_date", "valid_until"]

const COLUMN_LABELS: Record<string, string> = {
  number: "Numer",
  company: "Kontrahent",
  status: "Status",
  total_net: "Netto",
  total_gross: "Brutto",
  issue_date: "Data",
  valid_until: "Wazna do",
  owner: "Wlasciciel",
}

const columnOptions: ColumnOption[] = QUOTE_COLUMN_IDS.map((id) => ({
  id,
  label: COLUMN_LABELS[id],
}))

export function QuotesListPage() {
  const navigate = useNavigate()
  const { filters, setFilter, clearFilters, activeFilterCount } = useFilterParams(QUOTE_FILTER_KEYS)

  const { data, isLoading } = useQuotes({ ...filters })
  const sendQuote = useSendQuote()
  const acceptQuote = useAcceptQuote()
  const rejectQuote = useRejectQuote()
  const deleteQuote = useDeleteQuote()
  const newVersion = useNewQuoteVersion()

  const {
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    isColumnVisible,
  } = useColumnPreferences("quotes", QUOTE_COLUMN_IDS, DEFAULT_VISIBLE)

  const quotes = data?.data ?? []

  const handleSend = (q: Quote) => {
    sendQuote.mutate(q.id, {
      onSuccess: () => toast.success("Oferta wysłana"),
      onError: () => toast.error("Błąd wysyłania"),
    })
  }

  const handleAccept = (q: Quote) => {
    acceptQuote.mutate(q.id, {
      onSuccess: () => toast.success("Oferta zaakceptowana"),
    })
  }

  const handleReject = (q: Quote) => {
    rejectQuote.mutate(q.id, {
      onSuccess: () => toast.success("Oferta odrzucona"),
    })
  }

  const handleDelete = (q: Quote) => {
    deleteQuote.mutate(q.id, {
      onSuccess: () => toast.success("Oferta usunięta"),
    })
  }

  const handleNewVersion = (q: Quote) => {
    newVersion.mutate(q.id, {
      onSuccess: (res) => {
        toast.success("Utworzono nową wersję")
        navigate(`/quotes/${res.data.id}`)
      },
    })
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText size={20} className="text-primary" />
          <h1 className="page-title">Oferty</h1>
          <ColumnSelector
            columns={columnOptions}
            visibleCount={visibleCount}
            totalCount={totalCount}
            isColumnVisible={isColumnVisible}
            onToggleColumn={toggleColumn}
            onShowAll={showAll}
            onHideAll={hideAll}
          />
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            entity="quotes"
            filters={filters}
          />
          <Button size="sm" onClick={() => navigate("/quotes/new")}>
            <Plus size={14} className="mr-1" />
            Nowa oferta
          </Button>
        </div>
      </div>

      {/* Filters */}
      <FilterPanel activeCount={activeFilterCount} onClear={clearFilters}>
        <SelectFilter
          label="Status"
          value={filters.status || ""}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "draft", label: "Szkic" },
            { value: "sent", label: "Wyslana" },
            { value: "viewed", label: "Wyswietlona" },
            { value: "accepted", label: "Zaakceptowana" },
            { value: "rejected", label: "Odrzucona" },
            { value: "expired", label: "Wygasla" },
            { value: "cancelled", label: "Anulowana" },
          ]}
          placeholder="Wszystkie statusy"
        />
        <DateRangeFilter
          label="Data wystawienia"
          fromValue={filters.issue_date_from || ""}
          toValue={filters.issue_date_to || ""}
          onFromChange={(v) => setFilter("issue_date_from", v)}
          onToChange={(v) => setFilter("issue_date_to", v)}
        />
        <DateRangeFilter
          label="Wazna do"
          fromValue={filters.valid_until_from || ""}
          toValue={filters.valid_until_to || ""}
          onFromChange={(v) => setFilter("valid_until_from", v)}
          onToChange={(v) => setFilter("valid_until_to", v)}
        />
      </FilterPanel>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="surface-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/30">
                {isColumnVisible("number") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Numer</th>}
                {isColumnVisible("company") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Kontrahent</th>}
                {isColumnVisible("status") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Status</th>}
                {isColumnVisible("total_net") && <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Netto</th>}
                {isColumnVisible("total_gross") && <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Brutto</th>}
                {isColumnVisible("issue_date") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Data</th>}
                {isColumnVisible("valid_until") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Wazna do</th>}
                {isColumnVisible("owner") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Wlasciciel</th>}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => {
                const statusCfg = STATUS_CONFIG[q.status]
                return (
                  <tr
                    key={q.id}
                    className="border-t last:border-b-0 cursor-pointer transition-colors hover:bg-muted/30"
                    onClick={() => navigate(`/quotes/${q.id}`)}
                  >
                    {isColumnVisible("number") && <td className="px-4 py-3">
                      <span className="text-sm font-medium text-primary">
                        {q.number}
                      </span>
                      {q.version > 1 && (
                        <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground">
                          v{q.version}
                        </span>
                      )}
                    </td>}
                    {isColumnVisible("company") && <td className="px-4 py-3">
                      <span className="text-sm text-foreground">
                        {q.company?.name || "-"}
                      </span>
                    </td>}
                    {isColumnVisible("status") && <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        style={{ color: statusCfg.color, backgroundColor: statusCfg.bg, borderColor: statusCfg.color }}
                      >
                        {statusCfg.label}
                      </Badge>
                    </td>}
                    {isColumnVisible("total_net") && <td className="px-4 py-3 text-right">
                      <span className="text-sm tabular-nums text-foreground">
                        {formatCurrency(q.total_net)}
                      </span>
                    </td>}
                    {isColumnVisible("total_gross") && <td className="px-4 py-3 text-right">
                      <span className="text-sm tabular-nums font-medium text-foreground">
                        {formatCurrency(q.total_gross)}
                      </span>
                    </td>}
                    {isColumnVisible("issue_date") && <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground">
                        {formatDate(q.issue_date)}
                      </span>
                    </td>}
                    {isColumnVisible("valid_until") && <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground">
                        {formatDate(q.valid_until)}
                      </span>
                    </td>}
                    {isColumnVisible("owner") && <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground">
                        {q.owner ? `${q.owner.first_name} ${q.owner.last_name}` : "-"}
                      </span>
                    </td>}
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreHorizontal size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {q.status === "draft" && (
                            <DropdownMenuItem onClick={() => handleSend(q)}>
                              <Send size={14} className="mr-2" />
                              Wyślij
                            </DropdownMenuItem>
                          )}
                          {(q.status === "sent" || q.status === "viewed") && (
                            <>
                              <DropdownMenuItem onClick={() => handleAccept(q)}>
                                <CheckCircle size={14} className="mr-2" />
                                Zaakceptuj
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleReject(q)}>
                                <XCircle size={14} className="mr-2" />
                                Odrzuć
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={() => handleNewVersion(q)}>
                            <Copy size={14} className="mr-2" />
                            Nowa wersja
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(q)} className="text-red-600">
                            <Trash2 size={14} className="mr-2" />
                            Usuń
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                )
              })}
              {quotes.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <FileText size={48} className="mx-auto text-muted-foreground opacity-30" />
                    <p className="mt-4 text-sm font-medium text-foreground">
                      Brak ofert
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Utwórz pierwszą ofertę klikając przycisk powyżej
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
